import React from 'react';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import { FacilityReservation } from '../models/FacilityReservation';
import { Facility } from '../models/Facility';
import koLocale from '@fullcalendar/core/locales/ko';
import { useNavigate } from 'react-router-dom';
import './Calendar.css';

// 회원 등급 매핑
const memberTypeMap: { [key: string]: string } = {
  '1': '최고 관리자',
  '2': '일반 관리자',
  '3': '공사 관계자',
  '4': '센터 관계자',
  '5': '입주업체',
  '6': '입주협력업체',
  '7': '공공기관',
  '8': '항만근로자',
  '9': '일반 사용자'
};

const getCalendarSlotMaxTime = (operatingEndHour?: number) => {
  if (typeof operatingEndHour !== 'number' || Number.isNaN(operatingEndHour)) {
    return '18:00:00';
  }

  // 운영 종료시간을 달력 표기 기본값으로 사용할 때,
  // 종료 시간을 더 정확히 파악할 수 있도록 +1시간을 여유로 보여준다.
  const hour = Math.min(Math.max(operatingEndHour + 1, 0), 24);
  return `${hour}:00:00`;
};

interface CalendarProps {
  reservations: FacilityReservation[];
  facilities: Facility[];
  selectedFacilityId: string | null;
}

export const Calendar: React.FC<CalendarProps> = ({ 
  reservations, 
  facilities,
  selectedFacilityId 
}) => {
  const navigate = useNavigate();
  const selectedFacility = facilities.find(f => f.id === selectedFacilityId);

  // 예약 정보를 FullCalendar 이벤트 형식으로 변환
  const events = reservations
    .filter(reservation => 
      (reservation.status === 'pending' || reservation.status === 'approved') &&
      (selectedFacilityId && reservation.facilityId === selectedFacilityId) // 선택된 시설물의 예약만 표시
    )
    .map(reservation => {
      
      // 시작 시간과 종료 시간을 위한 새로운 Date 객체 생성
      const startDate = new Date(reservation.reservationDate);
      const endDate = new Date(reservation.reservationDate);
      startDate.setHours(reservation.startTimeHour, 0, 0);
      endDate.setHours(reservation.endTimeHour, 0, 0);

      const event = {
        id: reservation.id,
        title: `${reservation.userName}`,
        start: startDate,
        end: endDate,
        backgroundColor: reservation.status === 'pending' ? '#FCD34D' : '#34D399',
        borderColor: reservation.status === 'pending' ? '#F59E0B' : '#10B981',
        extendedProps: {
          status: reservation.status,
          organization: reservation.organization
        }
      };
      
      return event;
    });

  const handleEventClick = (clickInfo: any) => {
    navigate(`/admin/facility-reservations/${clickInfo.event.id}`);
  };

  // 시설물이 선택되지 않은 경우 안내 메시지 표시
  if (!selectedFacilityId) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="text-gray-500 mb-2">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">시설물을 선택해주세요</h3>
          <p className="text-gray-500">
            캘린더 뷰에서 예약 현황을 확인하려면<br />
            상단에서 시설물을 선택해주세요.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 선택된 시설물 정보 표시 */}
      {selectedFacility && (
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-2">{selectedFacility.name}</h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600">운영시간</p>
              <p>{selectedFacility.operatingHours.start}:00 ~ {selectedFacility.operatingHours.end}:00</p>
            </div>
            <div>
              <p className="text-gray-600">예약 단위</p>
              <p>{selectedFacility.reservationTimeUnit}시간</p>
            </div>
          </div>
        </div>
      )}

      {/* 캘린더 */}
      <div className="bg-white rounded-lg shadow fc-custom-theme">
        <FullCalendar
          plugins={[timeGridPlugin, dayGridPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'timeGridWeek,timeGridDay'
          }}
          locale={koLocale}
          allDaySlot={false}
          slotMinTime={selectedFacility ? `${selectedFacility.operatingHours.start}:00:00` : '09:00:00'}
          slotMaxTime={selectedFacility ? getCalendarSlotMaxTime(selectedFacility.operatingHours.end) : '18:00:00'}
          slotDuration="01:00:00"
          events={events}
          eventClick={handleEventClick}
          eventContent={(eventInfo) => {
            const status = eventInfo.event.extendedProps.status;
            const organization = eventInfo.event.extendedProps.organization;
            
            return (
              <div className={`p-1 text-xs rounded cursor-pointer transition-colors duration-200 hover:opacity-90
                ${status === 'pending' ? 'bg-yellow-100 border-yellow-400' : 'bg-green-100 border-green-400'}`}>
                <div className="font-semibold text-gray-900">
                  {eventInfo.event.title}
                  <span className="ml-1 font-normal text-gray-600">
                    ({status === 'pending' ? '대기중' : '승인됨'})
                  </span>
                </div>
                <div className="text-gray-600">
                  {eventInfo.event.start?.getHours()}:00 ~ {eventInfo.event.end?.getHours()}:00
                </div>
                <div className="text-gray-600">
                  소속: {organization || '소속 미지정'}
                </div>
              </div>
            );
          }}
          height="auto"
          dayMaxEvents={true}
          nowIndicator={true}
        />
      </div>
    </div>
  );
}; 
